import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useState, useEffect } from 'react'
import styled from 'styled-components'

import * as sessionUserSelector from 'selectors/sessionUser'

import {
  useCreateTicketProviderTicketFilterMutation,
  useUpdateTicketProviderTicketFilterMutation,
  useRemoveTicketProviderTicketFilterMutation,
  fetchTicketProviderTicketFilters,
} from 'api/ticketProviderTicketFilters'

import colors from 'components/utils/colors'
import { useModalFormOperation } from 'components/Shared/ModalForm/hooks'
import ticketFilterVisibility from 'constants/ticketFilterVisibility'

import { confirm } from 'components/Shared/ConfirmDialog/Index'
import LeftSidebar from 'components/Shared/Sidebar/LeftSidebar'
import IconLink from 'components/Shared/IconLink/Index'
import Tooltip from 'components/Shared/Tooltip/Index'
import TicketFiltersList from './TicketFiltersList'
import StatusesFilter from './StatusesFilter'
import AddEditFilterForm from './AddEditFilterForm/Index'

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  height: 48px;
  padding: 0 8px;
  background-color: #FFFFFF;
  line-height: 24px;
  border-bottom: 1px solid ${colors.secondary70};
`

const Title = styled.div`
  font-family: Exo;
  font-size: 20px;
  text-transform: uppercase;
  color: ${colors.secondary40};
`

const FilterCreateButton = styled(IconLink)`
  position: absolute;
  right: 36px;
`

const CloseButton = styled(IconLink)`
  position: absolute;
  right: 10px;
`

const FiltersSidebar = ({
  ticketProviderId, ticketFilters, activeTicketFilterIds,
  onFilterChange, onTicketFilterRemoving, isTicketFiltersFetching,
  partnerStatuses, isVisible, onClose, activeStatusFilters,
  selectedProviderTicketBoardId, providerTicketBoards,
}) => {
  const [isFilterFormVisible, setIsFilterFormVisible] = useState(false)
  const [editingFilter, setEditingFilter] = useState(null)
  const userIsAdmin = useSelector(sessionUserSelector.isAdmin)
  const canCreate = Boolean(userIsAdmin)
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [createTicketFilter] = useCreateTicketProviderTicketFilterMutation()
  const [updateTicketFilter] = useUpdateTicketProviderTicketFilterMutation()
  const [removeTicketFilter, { isLoading: isDeleting, isUninitialized: isUninitializedDelete }] = useRemoveTicketProviderTicketFilterMutation()

  const createTicketFilterModal = useModalFormOperation(createTicketFilter)
  const updateTicketFilterModal = useModalFormOperation(updateTicketFilter)

  useEffect(() => {
    if (ticketProviderId && isVisible) {
      const params = {
        ticketProviderId: ticketProviderId,
        providerTicketBoardId: selectedProviderTicketBoardId,
      }

      dispatch(fetchTicketProviderTicketFilters.initiate(params, { forceRefetch: true }))
    }
  }, [dispatch, ticketProviderId, isVisible, selectedProviderTicketBoardId])

  useEffect(() => {
    // effect triggers with isDeleting=false when component is created
    // and we don't need to call onTicketFilterRemoving() in this case
    if (!isUninitializedDelete) {
      onTicketFilterRemoving(isDeleting)
    }
  }, [isDeleting, isUninitializedDelete, onTicketFilterRemoving])

  const isActiveFilter = (filter) => {
    return activeTicketFilterIds.includes(filter.id)
  }

  const onFilterFormClose = () => {
    setEditingFilter(null)
    setIsFilterFormVisible(false)
  }

  const onFilterFormSubmit = (values) => {
    const params = {
      name: values.name,
      conditions: values.conditions,
      published: values.visibility === ticketFilterVisibility.ALL,
      advanced: values.advanced,
      query: values.query,
      filterableType: 'TicketProvider',
      filterableId: ticketProviderId,
      providerTicketBoardIds: providerTicketBoards.length > 1 ? values.providerTicketBoards : [selectedProviderTicketBoardId],
    }

    if (editingFilter) {
      updateTicketFilterModal({
        ticketProviderId: ticketProviderId,
        ticketFilterId: editingFilter.id,
        params: params,
      })
    } else {
      createTicketFilterModal({
        ticketProviderId: ticketProviderId,
        params: params,
      })
    }
  }

  const onToggleTicketsFilter = (filter, value) => {
    let newFilterIds
    if (value) {
      newFilterIds = [...activeTicketFilterIds, filter.id]
    } else {
      newFilterIds = activeTicketFilterIds.filter(item => item !== filter.id)
    }

    onFilterChange({
      ticket_filters: newFilterIds,
    })
  }

  const onChangeStatusFilter = (value) => {
    onFilterChange({
      status_filters: value,
    })
  }

  const onAddFilterClick = () => {
    setEditingFilter(null)
    setIsFilterFormVisible(true)
  }

  const onEditFilterClick = (editingFilter) => {
    setEditingFilter(editingFilter)
    setIsFilterFormVisible(true)
  }

  const onDeleteFilter = (filter) => {
    const filterIsActive = isActiveFilter(filter)
    const filterName = filter.name
    confirm({
      title: t('ticketProviderTicketsPage.removeFilterTitle'),
      message: t('ticketProviderTicketsPage.removeFilterMessage', { filterName }),
      t,
    }).then(
      () => {
        if (filterIsActive) {
          // deactivate filter to update tickets on delete
          onToggleTicketsFilter(filter, false)
        }
        removeTicketFilter({ ticketProviderId: ticketProviderId, ticketFilterId: filter.id })
        onFilterFormClose()
      },
      () => {}
    )
  }

  const Header = () => {
    return (
      <StyledHeader data-test='filters-header'>
        <Title>{t('ticketProviderTicketsPage.filters')}</Title>
        {canCreate && (
          <FilterCreateButton
            onClick={onAddFilterClick}
            iconName='icon-add'
            active={isFilterFormVisible && !editingFilter}
            data-tooltip-content={t('ticketProviderTicketsPage.createFilterTooltip')}
            data-tooltip-id='filter-tooltip'
          />
        )}
        <Tooltip place='left' id='filter-tooltip' />

        <CloseButton
          onClick={onClose}
          iconName='icon-delete-16'
          data-tooltip-content={t('common.close')}
          data-tooltip-id='filter-tooltip'
        />
      </StyledHeader>
    )
  }

  return (
    <LeftSidebar
      show={isVisible}
      dataTest='tickets-page-filters'
      headerComponent={<Header />}
    >
      <AddEditFilterForm
        visible={isFilterFormVisible}
        onSubmit={onFilterFormSubmit}
        onCloseForm={onFilterFormClose}
        editingFilter={editingFilter}
        onDeleteFilter={onDeleteFilter}
        ticketProviderId={ticketProviderId}
        partnerStatuses={partnerStatuses}
        providerTicketBoards={providerTicketBoards}
        selectedProviderTicketBoardId={selectedProviderTicketBoardId}
      />
      <StatusesFilter
        partnerStatuses={partnerStatuses}
        activeStatusFilters={activeStatusFilters}
        onChange={onChangeStatusFilter}
      />
      <TicketFiltersList
        ticketFilters={ticketFilters}
        activeTicketFilterIds={activeTicketFilterIds}
        onEditFilter={onEditFilterClick}
        onDeleteFilter={onDeleteFilter}
        onToggleFilter={onToggleTicketsFilter}
        editingFilter={editingFilter}
        isTicketFiltersFetching={isTicketFiltersFetching}
      />
    </LeftSidebar>
  )
}

FiltersSidebar.propTypes = {
  ticketProviderId: PropTypes.string.isRequired,
  ticketFilters: PropTypes.array.isRequired,
  activeTicketFilterIds: PropTypes.array.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onTicketFilterRemoving: PropTypes.func,
  isTicketFiltersFetching: PropTypes.bool,
  partnerStatuses: PropTypes.array.isRequired,
  isVisible: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  activeStatusFilters: PropTypes.array.isRequired,
  selectedProviderTicketBoardId: PropTypes.string,
  providerTicketBoards: PropTypes.array,
}

export default FiltersSidebar
